import { AfterViewInit, Directive, ElementRef, inject, input, Renderer2 } from '@angular/core';
import { PlatformService } from '../../services/platform.service';
import { fromEvent, merge } from 'rxjs';
import { UntilDestroy } from 'ngx-unificator/decorator';
import { untilDestroyed } from 'ngx-unificator/rxjs';
import { map, tap } from 'rxjs/operators';

export enum DefaultImageType {
  GAME = '/assets/img/loading.jpg'
}

@UntilDestroy()
@Directive({
  selector: '[defaultImage]',
})
export class DefaultImage implements AfterViewInit {

  private _el = inject(ElementRef);
  private _platform = inject(PlatformService);
  private _renderer = inject(Renderer2);

  /**
   * Alternative src for image. Set if src broken
   */
  readonly alternative = input('');

  /**
   * Default image src. Set if src and alternative src broken
   */
  readonly default = input<DefaultImageType | any>(null);

  /**
   * Target image element that will be checked
   */
  private _target: HTMLImageElement | any = null;

  /**
   * Current data-src for image
   */
  private _currentSrc = '';

  ngAfterViewInit(): void {
    this._target = this._el.nativeElement instanceof HTMLImageElement
      ? this._el.nativeElement
      : this._el.nativeElement.querySelector('img');

    if (!this._target || !this._platform.isBrowser) return;

    this._renderer.setStyle(this._target, 'opacity', '0');

    const error$ = fromEvent(this._target, 'error').pipe(
      map(() => 'error'),
    );

    const load$ = fromEvent(this._target, 'load').pipe(
      map(() => 'load'),
    );

    merge(error$, load$).pipe(
      untilDestroyed(this),
      tap((eventType) => {
        if (eventType === 'error') {
          this._renderer.setStyle(this._target, 'opacity', '0');
          if (this._currentSrc !== this.alternative() && this.alternative()) {
            this._currentSrc = this.alternative();
          } else if (this.default) {
            this._currentSrc = this.default();
          }
          this._renderer.setAttribute(this._target, 'src', this._currentSrc);
        } else if (eventType === 'load') {
          this._renderer.setStyle(this._target, 'opacity', '1');
          this._renderer.setStyle(this._target, 'transition', 'opacity .3s');
        }
      }),
    ).subscribe();
  }
}
